import { ShieldCheck, Compass, Network, GraduationCap } from "lucide-react";

const services = [
  {
    icon: ShieldCheck,
    title: "Crisis Leadership Advisory",
    desc: "Executive coaching and leadership briefings for senior teams facing high-consequence decisions. Build the judgment that holds up under pressure.",
    bullets: ["Tabletop facilitation", "Senior-team coaching", "Decision protocol design"],
  },
  {
    icon: Compass,
    title: "Organizational Resilience Assessments",
    desc: "Independent assessment of your organization's decision capacity, single points of failure, and continuity posture — before the next disruption arrives.",
    bullets: ["Decision-margin audit", "Continuity diagnostics", "Risk concentration review"],
  },
  {
    icon: Network,
    title: "Emergency Management Program Design",
    desc: "End-to-end design of preparedness, response, recovery, and continuity programs for government, higher education, healthcare, and public safety.",
    bullets: ["Plan & policy authoring", "Exercise design", "Cross-silo coordination"],
  },
  {
    icon: GraduationCap,
    title: "Leadership Development & Training",
    desc: "Custom curricula that translate operational experience and research into practical skills — for leaders at every level of the organization.",
    bullets: ["Cohort programs", "Executive workshops", "Train-the-trainer"],
  },
];

export default function Consulting() {
  return (
    <section
      id="consulting"
      data-testid="consulting-section"
      className="relative py-24 lg:py-32 border-t border-[#262626] bg-[#0a0a0a]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="overline overline-red mb-6">03 — Consulting</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.95]">
              Build decision
              <br />
              capacity <span className="text-[#d92d20]">before</span>
              <br />
              you need it.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-white/70 leading-relaxed">
              Dr. Avory works with senior leaders to strengthen the organizational
              fabric that determines performance under stress — communication,
              decision-making, coordination, and resilience.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 -m-px">
          {services.map((s, i) => (
            <ServiceCard key={i} {...s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ icon: Icon, title, desc, bullets, index }) {
  return (
    <div
      data-testid={`service-card-${index}`}
      className="cell group p-8 lg:p-10 bg-[#0a0a0a] hover:bg-[#101010]"
    >
      <div className="flex items-start justify-between mb-8">
        <div className="w-12 h-12 border border-[#262626] flex items-center justify-center group-hover:border-[#d92d20] group-hover:bg-[#d92d20] transition-colors">
          <Icon strokeWidth={1.5} className="w-5 h-5 text-white" />
        </div>
        <span className="font-mono text-xs text-white/30">0{index + 1}</span>
      </div>
      <h3 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight leading-tight mb-4">
        {title}
      </h3>
      <p className="text-white/65 leading-relaxed text-[15px] mb-6">{desc}</p>
      <ul className="space-y-2 border-t border-[#262626] pt-4">
        {bullets.map((b) => (
          <li key={b} className="text-sm text-white/70 flex items-center gap-3">
            <span className="w-1 h-1 bg-[#d92d20]" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}
